import { ApolloError } from '@apollo/client';
import {
  HubSurveyStatusCompleted,
  HubSurveyStatusIncomplete,
  useSurveyStatusQuery,
} from '../../../../graphQL';

export const SURVEY_STATUSES = {
  None: 'None',
  NotEligible: 'NotEligible',
  Incomplete: 'Incomplete',
  Completed: 'Completed',
} as const;
export type SurveyStatus = (typeof SURVEY_STATUSES)[keyof typeof SURVEY_STATUSES];

export type SurveyStatusData =
  | {
      status: typeof SURVEY_STATUSES.None | typeof SURVEY_STATUSES.NotEligible;
    }
  | ({
      status: typeof SURVEY_STATUSES.Incomplete;
    } & HubSurveyStatusIncomplete)
  | ({
      status: typeof SURVEY_STATUSES.Completed;
    } & HubSurveyStatusCompleted);

type UseGetSurveyStatusReturn =
  | {
      loading: true;
    }
  | {
      loading: false;
      success: false;
      error: ApolloError | string;
    }
  | {
      loading: false;
      success: true;
      survey: SurveyStatusData;
    };

export const useGetSurveyStatus = (skip = false): UseGetSurveyStatusReturn => {
  const { data, loading, error } = useSurveyStatusQuery({ skip });

  if (loading) {
    return { loading };
  }

  if (!data || error) {
    return { loading, success: false, error: error?.message ?? 'Unknown error' };
  }

  if (
    data.hubSurveyStatus.__typename === 'HubSurveyStatusError' &&
    data.hubSurveyStatus.errorCode === 'None'
  ) {
    return { loading: false, success: true, survey: { status: SURVEY_STATUSES.None } };
  }

  if (
    data.hubSurveyStatus.__typename === 'HubSurveyStatusError' &&
    data.hubSurveyStatus.errorCode === 'NotEligible'
  ) {
    return { loading: false, success: true, survey: { status: SURVEY_STATUSES.NotEligible } };
  }

  // unknown error code
  if (data.hubSurveyStatus.__typename === 'HubSurveyStatusError') {
    return {
      loading,
      success: false,
      error: `Unknown error: ${data.hubSurveyStatus.errorCode}`,
    };
  }

  if (data.hubSurveyStatus.__typename === 'HubSurveyStatusCompleted') {
    return {
      loading: false,
      success: true,
      survey: {
        status: SURVEY_STATUSES.Completed,
        ...data.hubSurveyStatus,
      },
    };
  }

  return {
    loading: false,
    success: true,
    survey: {
      status: SURVEY_STATUSES.Incomplete,
      ...data.hubSurveyStatus,
    },
  };
};
